import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Blueprint, UploadSimple, Camera, CircleNotch, Lightning, Flame,
  ListBullets, CurrencyDollar, Clock, WarningCircle, CheckCircle
} from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TakeoffMaterialRow({ item, idx }) {
  return (
    <div className="flex items-center justify-between p-2 border-b border-[#E5E5E5] last:border-0">
      <div className="flex-1">
        <p className="text-sm font-medium">{item.item || item.name}</p>
        {item.notes && <p className="text-[10px] text-[#404040]">{item.notes}</p>}
      </div>
      <div className="text-right shrink-0 ml-2">
        <span className="font-mono font-bold text-sm">{item.quantity}</span>
        <span className="font-mono-data text-[10px] text-[#404040] ml-1">{item.unit}</span>
      </div>
    </div>
  );
}

export default function PlansTakeoffPage() {
  const [trade, setTrade] = useState("electrical");
  const [propertyType, setPropertyType] = useState("residential");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
    setResult(null);
  };

  const runTakeoff = async () => {
    if (!photo) { toast.error("Upload or photograph your plans first"); return; }
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("trade", trade);
      formData.append("property_type", propertyType);
      formData.append("description", description);
      formData.append("plan_photo", photo);
      const res = await axios.post(`${API}/plans/takeoff`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(res.data);
      toast.success("Material takeoff complete");
    } catch {
      toast.error("Takeoff failed. Try a clearer photo.");
    }
    setLoading(false);
  };

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="plans-takeoff-page">
      <h1 className="text-xl font-black tracking-tight mb-1" style={{ fontFamily: "'Chivo', sans-serif" }}>
        Plans Takeoff
      </h1>
      <p className="text-xs text-[#404040] mb-2 font-mono-data">MATERIAL TAKEOFF FROM BLUEPRINTS</p>

      {/* Disclaimer */}
      <div className="p-2.5 bg-[#F4F4F5] border border-[#E5E5E5] rounded-sm mb-4">
        <p className="text-[10px] text-[#404040] leading-relaxed">
          <b>Disclaimer:</b> AI-generated material takeoffs are <b>estimates for reference only</b>. 
          Quantities, costs, and placement recommendations must be verified by a qualified 
          estimator or licensed contractor before purchasing or installation. WorkPro assumes 
          no liability for material shortages, cost overruns, or code violations resulting 
          from reliance on these estimates.
        </p>
      </div>

      {/* Settings */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <Select value={trade} onValueChange={v => { setTrade(v); setResult(null); }}>
          <SelectTrigger data-testid="takeoff-trade-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="electrical">Electrical</SelectItem>
            <SelectItem value="hvac">HVAC</SelectItem>
          </SelectContent>
        </Select>
        <Select value={propertyType} onValueChange={setPropertyType}>
          <SelectTrigger data-testid="takeoff-property-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="residential">Residential</SelectItem>
            <SelectItem value="commercial">Commercial</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Plan Upload */}
      <div className="mb-3" data-testid="plan-upload-area">
        {preview ? (
          <div className="relative rounded-sm overflow-hidden border border-[#E5E5E5]">
            <img src={preview} alt="Plans" className="w-full h-56 object-contain bg-[#F4F4F5]" />
            <div className="absolute top-2 right-2">
              <button onClick={() => { setPhoto(null); setPreview(null); setResult(null); }}
                className="w-7 h-7 bg-black/60 text-white rounded-sm flex items-center justify-center text-xs">X</button>
            </div>
            <div className="absolute bottom-2 left-2 glass-overlay px-2 py-1 rounded-sm">
              <span className="text-[10px] text-white font-mono-data">{trade.toUpperCase()} | {propertyType.toUpperCase()}</span>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-[#E5E5E5] rounded-sm p-8 text-center">
            <Blueprint size={40} className="mx-auto mb-3 text-[#E5E5E5]" />
            <p className="text-sm text-[#404040] mb-3">Upload blueprints or photograph plans with your phone</p>
            <div className="flex gap-2 justify-center">
              <button onClick={() => fileRef.current?.click()} data-testid="upload-plans-btn"
                className="touch-target px-4 py-2 bg-[#F4F4F5] text-[#0A0A0A] text-xs font-bold rounded-sm border border-[#E5E5E5] hover:border-[#0A0A0A] flex items-center gap-1.5">
                <UploadSimple size={14} /> Upload
              </button>
              <button onClick={() => cameraRef.current?.click()} data-testid="photo-plans-btn"
                className="touch-target px-4 py-2 bg-[#002FA7] text-white text-xs font-bold rounded-sm hover:bg-[#001D6C] flex items-center gap-1.5">
                <Camera size={14} /> Photo
              </button>
            </div>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
      </div>

      <textarea data-testid="takeoff-description" value={description} onChange={e => setDescription(e.target.value)}
        placeholder="Notes about the plans (e.g., 2-story home, 2400sqft, new construction)..."
        className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] resize-none h-14 mb-3" />

      <button data-testid="run-takeoff-btn" onClick={runTakeoff} disabled={loading || !photo}
        className="w-full py-3 bg-[#34C759] text-white text-sm font-bold rounded-sm hover:bg-[#2AA04A] disabled:opacity-40 flex items-center justify-center gap-2">
        {loading ? <><CircleNotch size={16} className="animate-spin" /> Analyzing Plans...</> :
        <><ListBullets size={16} /> Generate Material Takeoff</>}
      </button>

      {/* Results */}
      {result && (
        <div className="mt-4 space-y-3 animate-slide-up" data-testid="takeoff-result">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="wp-card">
              <div className="metric-label flex items-center gap-1"><CurrencyDollar size={10} /> EST. COST</div>
              <div className="metric-value">${(result.estimated_cost || 0).toLocaleString()}</div>
            </div>
            <div className="wp-card">
              <div className="metric-label flex items-center gap-1"><Clock size={10} /> EST. HOURS</div>
              <div className="metric-value text-[#002FA7]">{result.estimated_labor_hours || "N/A"}</div>
            </div>
          </div>

          {result.summary && (
            <div className="wp-card"><p className="text-xs text-[#404040]">{result.summary}</p></div>
          )}

          {/* Materials */}
          {result.materials?.length > 0 && (
            <div>
              <h3 className="font-mono-data text-[10px] text-[#0A0A0A] mb-1">MATERIALS ({result.materials.length})</h3>
              <div className="wp-card p-0">{result.materials.map((m,i) => <TakeoffMaterialRow key={i} item={m} />)}</div>
            </div>
          )}

          {/* Placement Notes (for rough-in inspection) */}
          {result.placement_notes?.length > 0 && (
            <div>
              <h3 className="font-mono-data text-[10px] text-[#002FA7] mb-1 flex items-center gap-1">
                <CheckCircle size={10} /> PLACEMENT NOTES (ROUGH-IN)
              </h3>
              <div className="wp-card">
                <ul className="space-y-1">
                  {result.placement_notes.map((n,i) => <li key={i} className="text-[11px] text-[#404040] flex items-start gap-1"><span className="text-[#002FA7] mt-0.5">-</span> {n}</li>)}
                </ul>
              </div>
            </div>
          )}

          {/* Code Concerns */}
          {result.code_concerns?.length > 0 && (
            <div>
              <h3 className="font-mono-data text-[10px] text-[#FF5A00] mb-1 flex items-center gap-1">
                <WarningCircle size={10} /> CODE CONCERNS
              </h3>
              <div className="wp-card border-l-2 border-l-[#FF5A00]">
                <ul className="space-y-1">
                  {result.code_concerns.map((c,i) => <li key={i} className="text-[11px] text-[#404040] flex items-start gap-1"><WarningCircle size={10} className="text-[#FF5A00] mt-0.5 shrink-0" /> {c}</li>)}
                </ul>
              </div>
            </div>
          )}

          {/* Raw fallback */}
          {result.raw_response && (!result.materials || result.materials.length === 0) && (
            <div className="wp-card">
              <span className="font-mono-data text-[10px] text-[#404040]">AI ANALYSIS</span>
              <p className="text-xs text-[#404040] mt-1 whitespace-pre-wrap">{result.raw_response}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
